import { useEffect, useState } from 'react';
// @mui
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Grid from '@mui/material/Grid2';
import Iconify from 'src/components/iconify';
// utils
import axios, { API_ENDPOINTS } from 'src/utils/axios';
//
import { useOnboarding } from './onboarding-context';

// §7's welcome modal: business type / role / team size -> sample-or-blank ->
// tour preview. Business types reuse the real ProjectTypeDefinition registry
// (Task 11.1) the New Project wizard already fetches from, and roles reuse
// the real 12-role ACCESS_STD list (Task 1.2) via GET /api/onboarding/roles
// — no second taxonomy invented for either.

const TEAM_SIZE_OPTIONS = [
  { value: 'solo', label: 'Just me' },
  { value: 'small', label: '2–10 people' },
  { value: 'medium', label: '11–50 people' },
  { value: 'large', label: '50+ people' },
];

const STEPS = ['Business type', 'Your role', 'Team size', 'Sample or blank', "You're set"];

export default function WelcomeModal() {
  const { state, loading, completeWelcome, startTour, tours } = useOnboarding();
  const [activeStep, setActiveStep] = useState(0);
  const [businessType, setBusinessType] = useState('');
  const [onboardingRole, setOnboardingRole] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [mode, setMode] = useState('');
  const [businessTypeOptions, setBusinessTypeOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // Once the user finishes step 4 (completeWelcome has already flipped the
  // server's needsWelcome to false), keep the dialog open locally for the
  // "you're set / take the tour" step instead of letting it vanish the
  // instant the underlying state refreshes.
  const [finished, setFinished] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const needsWelcome = !loading && !!state && state.needsWelcome;
  const open = (needsWelcome || finished) && !dismissed;

  useEffect(() => {
    if (!needsWelcome) return;
    axios.get(API_ENDPOINTS.projectTypeDefinitions.list, { params: { category: 'business' } })
      .then((res) => setBusinessTypeOptions((res.data?.data || []).map((t) => ({ value: t.key, label: t.label }))))
      .catch(() => setBusinessTypeOptions([]));
    axios.get(API_ENDPOINTS.onboarding.roles)
      .then((res) => setRoleOptions(res.data?.data || []))
      .catch(() => setRoleOptions([]));
  }, [needsWelcome]);

  if (!open) return null;

  async function handleFinish(selectedMode) {
    setMode(selectedMode);
    setSubmitting(true);
    try {
      await completeWelcome({ businessType, onboardingRole, teamSize, mode: selectedMode });
      setFinished(true);
      setActiveStep(4);
    } finally {
      setSubmitting(false);
    }
  }

  function handleStartTour() {
    if (tours[0]) startTour(tours[0].key);
    setDismissed(true);
  }

  return (
    <Dialog open fullWidth maxWidth="sm" disableEscapeKeyDown>
      <DialogTitle>Welcome to Projects</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 1 }}>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">What best describes your business?</Typography>
            <Grid container spacing={1.5}>
              {businessTypeOptions.map((opt) => (
                <Grid key={opt.value} size={{ xs: 6 }}>
                  <Card variant="outlined" sx={businessType === opt.value ? { borderColor: 'primary.main', boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` } : {}}>
                    <CardActionArea sx={{ p: 1.5 }} onClick={() => setBusinessType(opt.value)}>
                      <Typography variant="body2">{opt.label}</Typography>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Stack>
        )}

        {activeStep === 1 && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">What's your role on most projects?</Typography>
            <Grid container spacing={1.5}>
              {roleOptions.map((opt) => (
                <Grid key={opt.value} size={{ xs: 6 }}>
                  <Card variant="outlined" sx={onboardingRole === opt.value ? { borderColor: 'primary.main', boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` } : {}}>
                    <CardActionArea sx={{ p: 1.5 }} onClick={() => setOnboardingRole(opt.value)}>
                      <Typography variant="body2">{opt.label}</Typography>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Stack>
        )}

        {activeStep === 2 && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">How big is your team?</Typography>
            <Grid container spacing={1.5}>
              {TEAM_SIZE_OPTIONS.map((opt) => (
                <Grid key={opt.value} size={{ xs: 6 }}>
                  <Card variant="outlined" sx={teamSize === opt.value ? { borderColor: 'primary.main', boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` } : {}}>
                    <CardActionArea sx={{ p: 1.5 }} onClick={() => setTeamSize(opt.value)}>
                      <Typography variant="body2">{opt.label}</Typography>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Stack>
        )}

        {activeStep === 3 && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Start with a sample project you can explore freely, or jump straight to a blank one?
            </Typography>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 6 }}>
                <Card variant="outlined">
                  <CardActionArea sx={{ p: 2 }} onClick={() => handleFinish('sample')} disabled={submitting}>
                    <Iconify icon="solar:magic-stick-3-bold-duotone" width={32} />
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>Show me a sample</Typography>
                    <Typography variant="caption" color="text.secondary">A real, pre-filled project to click around in.</Typography>
                  </CardActionArea>
                </Card>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Card variant="outlined">
                  <CardActionArea sx={{ p: 2 }} onClick={() => handleFinish('blank')} disabled={submitting}>
                    <Iconify icon="solar:document-add-bold-duotone" width={32} />
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>Start blank</Typography>
                    <Typography variant="caption" color="text.secondary">I'll create my own project.</Typography>
                  </CardActionArea>
                </Card>
              </Grid>
            </Grid>
          </Stack>
        )}

        {activeStep === 4 && (
          <Stack spacing={2} alignItems="center" sx={{ py: 3, textAlign: 'center' }}>
            <Iconify icon="solar:check-circle-bold-duotone" width={48} />
            <Typography variant="h6">You&apos;re all set{mode === 'sample' ? ' — your sample project is ready' : ''}.</Typography>
            <Typography variant="body2" color="text.secondary">
              Want a quick tour of the project hub? You can always find this again from the Help menu.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {activeStep > 0 && activeStep < 3 && (
          <Button onClick={() => setActiveStep((s) => s - 1)}>Back</Button>
        )}
        {activeStep < 3 && (
          <Button
            variant="contained"
            disabled={(activeStep === 0 && !businessType) || (activeStep === 1 && !onboardingRole) || (activeStep === 2 && !teamSize)}
            onClick={() => setActiveStep((s) => s + 1)}
          >
            Next
          </Button>
        )}
        {activeStep === 4 && (
          <>
            <Button onClick={() => setDismissed(true)}>Maybe later</Button>
            <Button onClick={handleStartTour} variant="contained">Take the tour</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
